import * as React from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { Form, DatePicker, Input, Select, Button, Tooltip } from "antd";
import { RangePickerValue } from "antd/lib/date-picker/interface";
import { TradingInstructionDirection, TradeSubType } from "swan-public-api-client";
import { SelectValue } from "antd/lib/select";
import { getRangePickerValue } from "../../Input/DatePickerExtensions";
import { ReactNode } from "react";

export interface SearchParameters {
  from: Date;
  to: Date;
  marketName?: string;
  direction?: TradingInstructionDirection;
  valueDateFrom?: Date;
  valueDateTo?: Date;
  tradeSubType?: TradeSubType;
}

export interface SpotFxTradeHistorySearchFormValues {
  from?: Date;
  to?: Date;
  marketName?: string;
  direction?: TradingInstructionDirection;
  valueDateFrom?: Date;
  valueDateTo?: Date;
  tradeSubType?: TradeSubType;
}

// tslint:disable-next-line: no-empty-interface
interface State extends SpotFxTradeHistorySearchFormValues {}

interface Props extends WrappedComponentProps {
  values?: SpotFxTradeHistorySearchFormValues;
  onSearch: (searchParameters: SearchParameters) => void;
  extra?: string|ReactNode;
}

class SpotFxTradeHistorySearchFormCmp extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      ...props.values
    };
  }

  public render() {
    const canSearch = this.state.from !== undefined && this.state.to !== undefined;
    const searchButton = (
      <Button
        type="primary"
        icon="search"
        disabled={!canSearch}
        onClick={() => this.onSearchClick()}
      >
        {this.props.intl.formatMessage({ id: "tradeHistorySearchForm.search" })}
      </Button>
    );

    return (
      <Form layout="inline">
        <Form.Item label={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.date" })}>
          <DatePicker.RangePicker
            showTime
            allowClear={false}
            placeholder={[this.props.intl.formatMessage({ id: "tradeHistorySearchForm.from" }), this.props.intl.formatMessage({ id: "tradeHistorySearchForm.to" })]}
            disabledDate={x => (x === null || x === undefined) || x.toDate() > new Date()}
            onChange={dates => this.onDateChange(dates)}
            value={getRangePickerValue(this.state.from, this.state.to)}
          />
        </Form.Item>
        <Form.Item label={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.market" })}>
          <Input
            placeholder={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.name" })}
            allowClear
            onChange={event => this.onMarketChange(event)}
            onPressEnter={() => this.onSearchClick()}
            value={this.state.marketName}
          />
        </Form.Item>
        <Form.Item label={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.direction" })}>
          <Select
            allowClear
            style={{ width: 80 }}
            onChange={(selectedValue: SelectValue) => this.onDirectionChange(selectedValue)}
            value={this.state.direction}
          >
            <Select.Option value={TradingInstructionDirection.Buy}>{this.props.intl.formatMessage({ id: "tradingInstructionDirection.buy" })}</Select.Option>
            <Select.Option value={TradingInstructionDirection.Sell}>{this.props.intl.formatMessage({ id: "tradingInstructionDirection.sell" })}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.tradeSubType" })}>
          <Select
            allowClear
            style={{ width: 130 }}
            onChange={(selectedValue: SelectValue) => this.onTradeSubTypeChange(selectedValue)}
            value={this.state.tradeSubType}
          >
            <Select.Option value={TradeSubType.New}>{this.props.intl.formatMessage({ id: "tradeSubType.new" })}</Select.Option>
            <Select.Option value={TradeSubType.Roll}>{this.props.intl.formatMessage({ id: "tradeSubType.roll" })}</Select.Option>
            <Select.Option value={TradeSubType.FxConversionOpen}>{this.props.intl.formatMessage({ id: "tradeSubType.fxConversionOpen" })}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.valueDate" })}>
          <DatePicker.RangePicker
            showTime={false}
            allowClear={true}
            placeholder={[this.props.intl.formatMessage({ id: "tradeHistorySearchForm.from" }), this.props.intl.formatMessage({ id: "tradeHistorySearchForm.to" })]}
            onChange={dates => this.onValueDateChange(dates)}
            value={getRangePickerValue(this.state.valueDateFrom, this.state.valueDateTo)}
          />
        </Form.Item>
        <Form.Item>
          {canSearch
            ? searchButton
            : (
              <Tooltip title={this.props.intl.formatMessage({ id: "tradeHistorySearchForm.fromAndToDatesRequiredValidationMessage" })}>{searchButton}</Tooltip>
            )
          }
        </Form.Item>
        {
          this.props.extra
            ? <Form.Item style={{ float: "right" }}>{this.props.extra}</Form.Item>
            : ""
        }
      </Form>
    );
  }

  private onDateChange(dates: RangePickerValue) {
    const pickerFrom = dates[0];
    const from = pickerFrom?.toDate();

    if (dates.length === 1) {
      this.setState({
        ...this.state,
        from
      });
      return;
    }

    const pickerTo = dates[1];
    const to = pickerTo?.toDate();

    this.setState({
      ...this.state,
      from,
      to
    });
  }

  private onMarketChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    const marketName = value.length > 0
      ? value
      : undefined;

    this.setState({
      ...this.state,
      marketName
    });
  }

  private onDirectionChange(selectedValue: SelectValue) {
    const direction = selectedValue as TradingInstructionDirection | undefined;

    this.setState({
      ...this.state,
      direction
    });
  }

  private onTradeSubTypeChange(selectedValue: SelectValue) {
    const tradeSubType = selectedValue as TradeSubType | undefined;

    this.setState({
      ...this.state,
      tradeSubType
    });
  }

  private onValueDateChange(dates: RangePickerValue) {
    const pickerValueDateFrom = dates[0];
    const valueDateFrom = pickerValueDateFrom?.startOf("date").toDate();

    if (dates.length === 1) {
      this.setState({
        ...this.state,
        valueDateFrom
      });
      return;
    }

    const pickerValueDateTo = dates[1];
    const valueDateTo = pickerValueDateTo?.endOf("date").toDate();

    this.setState({
      ...this.state,
      valueDateFrom,
      valueDateTo
    });
  }

  private onSearchClick() {
    if (this.state.from !== undefined && this.state.to !== undefined) {
      const searchParameters: SearchParameters = {
        ...this.state,
        from: this.state.from,
        to: this.state.to
      };

      this.props.onSearch(searchParameters);
    }
  }
}

export const SpotFxTradeHistorySearchForm = injectIntl(SpotFxTradeHistorySearchFormCmp);
